import { css, jsx } from '@emotion/react';
import { Children, isValidElement } from 'react';
import { labelStyles } from '@zapier/style-encapsulation';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import { Typography } from '../../../theme/Typography.es.js';
import '../../../theme/ZinniaStylesReset.es.js';

const Vars = createUniqueCustomProperties("AvatarsWithText", [
  "textDisplay",
  "textFontSizeLight",
  "textLineHeightLight",
  "textFontSizeHeavy",
  "textLineHeightHeavy"
]);
const Styles = labelStyles("AvatarsWithText", {
  root: (shouldRenderGap) => css`
    ${Vars.textDisplay}: inline-block;
    ${Vars.textFontSizeLight}: 12px;
    ${Vars.textLineHeightLight}: 15px;
    ${Vars.textFontSizeHeavy}: 12px;
    ${Vars.textLineHeightHeavy}: 15px;

    display: grid;
    grid-template-columns: auto 1fr;
    gap: ${shouldRenderGap ? "0px" : "5px"};
    align-items: center;

    &[data-multiline] {
      ${Vars.textDisplay}: block;
    }

    &[data-size='18'] {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint3.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint3.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint3Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint3Bold.lineHeight};
    }
    &[data-size='18'] > *:last-of-type {
      top: -1px;
    }

    &[data-size='25']:not([data-multiline]),
    &[data-size='40']:not([data-multiline]) {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint1.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint1.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint1Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint1Bold.lineHeight};
    }

    &[data-size='25'][data-multiline] {
      align-items: start;
      ${Vars.textFontSizeLight}: ${Typography.smallPrint1.fontSize};
      ${Vars.textLineHeightLight}: 17px;
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint1Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint1Bold.lineHeight};
    }

    &[data-size='40'][data-multiline] {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint1.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint1.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.paragraph3Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.paragraph3Bold.lineHeight};
    }
    &[data-size='40'] {
      gap: ${shouldRenderGap ? "0px" : "10px"};
    }
  `,
  avatars: css`
    display: block;
  `,
  content: css`
    display: block;
    position: relative;
    line-height: max(
      var(${Vars.textLineHeightLight}),
      var(${Vars.textLineHeightHeavy})
    );
  `,
  text: css`
    display: var(${Vars.textDisplay});
    &[data-kind='light'] {
      color: ${Colors.neutral700};
      font-weight: 400;
      font-size: calc(1px * var(${Vars.textFontSizeLight}));
      line-height: var(${Vars.textLineHeightLight});
    }
    &[data-kind='heavy'] {
      color: ${Colors.neutral800};
      font-weight: 700;
      font-size: calc(1px * var(${Vars.textFontSizeHeavy}));
      line-height: var(${Vars.textLineHeightHeavy});
    }
  `
});
const AvatarText = (props) => {
  return jsx("span", {
    "aria-label": props.ariaLabel,
    css: Styles.text,
    "data-kind": props.kind,
    "data-zds": true,
    // `role="text"` isn't technically valid (yet), but it allows
    // VoiceOver to read the content more clearly and prevents it
    // from splitting text. Axe also complains about this, but will
    // stop when the extension uses `axe-core@4.2.0`. See issue:
    // https://github.com/dequelabs/axe-core/issues/1597
    // eslint-disable-next-line jsx-a11y/aria-role
    role: "text"
  }, props.children);
};
const AvatarsWithText = (props) => {
  var _a, _b;
  const childArray = Children.toArray(props.avatars);
  const childCount = isValidElement(childArray[0]) ? (_b = (_a = childArray[0].props) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.length : 0;
  const shouldRenderGap = childCount > 3 && props.size !== 40;
  return jsx(
    "div",
    { "aria-label": props.ariaLabel, css: Styles.root(shouldRenderGap), "data-multiline": props.multiline ? "" : void 0, "data-size": props.size, "data-zds": true },
    jsx("div", { css: Styles.avatars, "data-zds": true }, props.avatars),
    jsx("div", { css: Styles.content, "data-zds": true }, props.children)
  );
};

export { AvatarText, AvatarsWithText };
//# sourceMappingURL=AvatarsWithText.es.js.map
